import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '../tokens/cn';
import { focusRing, transitionBase } from '../tokens/variants';

export interface SwitchProps
  extends ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  size?: 'sm' | 'md' | 'lg';
}

const sizeMap = {
  sm: { root: 'h-4 w-7', thumb: 'h-3 w-3 data-[state=checked]:translate-x-3' },
  md: { root: 'h-5 w-9', thumb: 'h-4 w-4 data-[state=checked]:translate-x-4' },
  lg: { root: 'h-6 w-11', thumb: 'h-5 w-5 data-[state=checked]:translate-x-5' },
};

export const Switch = forwardRef<
  ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(({ className, size = 'md', ...props }, ref) => {
  const { root, thumb } = sizeMap[size];
  return (
    <SwitchPrimitive.Root
      ref={ref}
      className={cn(
        'shrink-0 inline-flex items-center rounded-tk-full p-0.5 cursor-pointer',
        'border border-transparent',
        'bg-[var(--tk-surface-border-strong)]',
        'data-[state=checked]:bg-[var(--tk-brand-bg)]',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        focusRing,
        transitionBase,
        root,
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none rounded-full bg-[var(--tk-brand-fg)] shadow-sm',
          'translate-x-0',
          'transition-transform duration-200 ease-out',
          thumb,
        )}
      />
    </SwitchPrimitive.Root>
  );
});

Switch.displayName = 'Switch';
